import { useId } from 'react'
import type { ChipId } from '@/services/feedbackContext/chips'
import type { FeedbackSnapshot } from '@/services/feedbackContext/environment'
import { Button, Textarea } from '@/components/ui'
import { cn } from '@/utils'
import type { Card } from './conversation'
import { AttachedRow } from './AttachedRow'
import { KindChips } from './KindChips'
import { ADD_MORE, ATTEMPTING_LABEL, KIND_WORDS, SEND, SUMMARY_LABEL } from './helpFeedbackText'

const QUIET = 'min-h-0 resize-none border-transparent bg-transparent px-1.5 py-1 shadow-none hover:border-border focus-visible:border-ring'

/** What the panel will send (decision 12): editable in place while live, read-only once dimmed or sent. */
export function SummaryCard({
  lead, card, live, snapshot, removed, sending, disabled, onEdit, onRemoveChip, onSend, onAddMore,
}: {
  lead: string
  card: Card
  live: boolean
  snapshot: FeedbackSnapshot
  removed: ChipId[]
  sending: boolean
  disabled: boolean
  onEdit: (patch: Partial<Card>) => void
  onRemoveChip: (chip: ChipId) => void
  onSend: () => void
  onAddMore: () => void
}) {
  const id = useId()
  return (
    <div className={cn('space-y-2', !live && 'opacity-60')}>
      <p className="text-sm">{lead}</p>
      <div className="space-y-3 rounded-lg border border-section-border bg-card p-3 shadow-xs">
        {live ? (
          <>
            <KindChips value={card.kind} onChange={(kind) => onEdit({ kind })} disabled={sending} />
            <div>
              <label htmlFor={`${id}-summary`} className="sr-only">{SUMMARY_LABEL}</label>
              <Textarea
                id={`${id}-summary`}
                rows={2}
                value={card.summary}
                onChange={(e) => onEdit({ summary: e.target.value })}
                className={cn(QUIET, 'text-sm font-medium')}
              />
            </div>
            <div>
              <label htmlFor={`${id}-attempting`} className="px-1.5 text-xs text-muted-foreground">{ATTEMPTING_LABEL}</label>
              <Textarea
                id={`${id}-attempting`}
                rows={1}
                value={card.attempting}
                onChange={(e) => onEdit({ attempting: e.target.value })}
                className={cn(QUIET, 'text-sm')}
              />
            </div>
          </>
        ) : (
          <>
            <p className="text-xs font-medium text-muted-foreground">{KIND_WORDS[card.kind]}</p>
            <p className="text-sm font-medium whitespace-pre-wrap">{card.summary}</p>
            {card.attempting && (
              <p className="text-sm">
                <span className="text-xs text-muted-foreground">{ATTEMPTING_LABEL}</span> {card.attempting}
              </p>
            )}
          </>
        )}
        <AttachedRow snapshot={snapshot} removed={removed} onRemove={onRemoveChip} readOnly={!live} />
      </div>
      {live && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onSend} disabled={disabled} isLoading={sending}>{SEND}</Button>
          <Button size="sm" variant="ghost" onClick={onAddMore} disabled={disabled}>{ADD_MORE}</Button>
        </div>
      )}
    </div>
  )
}
